import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import React, { FunctionComponent, ReactNode, useState } from "react";
import XIcon from "@mui/icons-material/Close";

interface Props {
  description?: string | ReactNode;
  destructive?: boolean;
  onClose: () => void;
  onYes: () => void;
  noText?: string;
  title?: string | ReactNode;
  yesText?: string;
  yesIcon?: ReactNode;
  yesDisabled?: boolean;
  showCloseButton?: boolean;
}

export const ConfirmationDialog: FunctionComponent<Props> = ({
  description,
  destructive = false,
  onClose,
  onYes,
  noText,
  title,
  yesText,
  yesIcon,
  yesDisabled = false,
  showCloseButton = false,
}) => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  return (
    <Dialog open onClose={onClose}>
      <DialogTitle className="flex justify-between items-center gap-2">
        {title ?? t("components.ConfirmationDialog.title")}
        {showCloseButton && (
          <IconButton
            aria-label={t("utils.actions.close")}
            onClick={onClose}
            size="small"
          >
            <XIcon />
          </IconButton>
        )}
      </DialogTitle>
      {description && (
        <DialogContent dividers className="flex flex-col gap-4 items-stretch">
          {description}
        </DialogContent>
      )}
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          {noText ?? t("utils.actions.cancel")}
        </Button>
        <Button
          color={destructive ? "error" : "secondary"}
          variant="contained"
          loading={isLoading}
          disabled={yesDisabled}
          onClick={async () => {
            setLoading(true);
            await onYes();
            setLoading(false);
            onClose();
          }}
          startIcon={yesIcon}
          loadingPosition={yesIcon ? "start" : "center"}
        >
          {yesText ?? t("utils.actions.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
